'use client';
import React from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';

interface NavItem {
  label: string;
  icon: string;
  href: string;
  badge?: number;
  group?: string;
}

const NAV_ITEMS: NavItem[] = [
  { label: 'Dashboard', icon: 'HomeIcon', href: '/', group: 'core' },
  { label: 'Item Master', icon: 'CubeIcon', href: '/item-master-management', group: 'inventory' },
  { label: 'Stock Positions', icon: 'ChartBarIcon', href: '/stock-positions', group: 'inventory' },
  { label: 'Stock Count', icon: 'ClipboardDocumentCheckIcon', href: '/stock-count', group: 'inventory' },
  { label: 'GR & Issuance', icon: 'ArrowsRightLeftIcon', href: '/grn-management', badge: 4, group: 'transactions' },
  { label: 'Goods Issuance', icon: 'TruckIcon', href: '/goods-issuance-management', badge: 2, group: 'transactions' },
  { label: 'Reports', icon: 'DocumentChartBarIcon', href: '/reports', group: 'intelligence' },
  { label: 'QHSE', icon: 'ShieldCheckIcon', href: '/qhse', badge: 3, group: 'intelligence' },
  { label: 'BOM / Projects', icon: 'ClipboardDocumentListIcon', href: '/bom', group: 'intelligence' },
  { label: 'Settings', icon: 'Cog6ToothIcon', href: '/settings', group: 'system' },
];

const GROUP_LABELS: Record<string, string> = {
  core: 'Overview',
  inventory: 'Inventory',
  transactions: 'Transactions',
  intelligence: 'Intelligence',
  system: 'System',
};

interface SidebarProps {
  collapsed: boolean;
  mobileOpen: boolean;
  onMobileClose: () => void;
  currentPath: string;
}

export default function Sidebar({ collapsed, mobileOpen, onMobileClose, currentPath }: SidebarProps) {
  const groups = Array.from(new Set(NAV_ITEMS.map((i) => i.group)));

  return (
    <aside
      className={[
        'flex flex-col z-50 sidebar-transition',
        'fixed lg:relative h-full',
        collapsed ? 'w-[60px]' : 'w-[220px]',
        mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
      ].join(' ')}
      style={{ background: 'linear-gradient(180deg, #0D2240 0%, #132D4F 60%, #0F2845 100%)' }}
    >
      {/* Logo */}
      <div className={`flex items-center gap-3 border-b border-white/8 min-h-[60px] ${collapsed ? 'justify-center px-0' : 'px-4'}`}>
        <AppLogo size={30} className="shrink-0" />
        {!collapsed && (
          <div>
            <span className="font-700 text-[15px] text-white tracking-tight leading-none block">Opsivo</span>
            <span className="text-[9px] text-white/40 tracking-widest uppercase">Operations</span>
          </div>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto scrollbar-thin py-3 px-2">
        {groups.map((group) => {
          const items = NAV_ITEMS.filter((i) => i.group === group);
          return (
            <div key={`group-${group}`} className="mb-5">
              {!collapsed && (
                <p className="text-[9px] font-700 uppercase tracking-[0.12em] text-white/25 px-2.5 mb-1.5">
                  {GROUP_LABELS[group ?? ''] ?? group}
                </p>
              )}
              {items.map((item) => {
                const isActive = currentPath === item.href || (item.href !== '/' && currentPath.startsWith(item.href));
                return (
                  <Link
                    key={`nav-${item.href}`}
                    href={item.href}
                    title={collapsed ? item.label : undefined}
                    className={[
                      'relative flex items-center gap-2.5 py-2 rounded-lg mb-0.5 text-[13px] font-500 transition-all duration-150',
                      'hover:bg-white/8 active:scale-[0.98]',
                      collapsed ? 'justify-center px-0 mx-1' : 'px-2.5',
                      isActive
                        ? 'bg-white/10 text-white font-600' :'text-white/55 hover:text-white/80',
                    ].join(' ')}
                  >
                    {isActive && (
                      <span className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 bg-accent rounded-r-full" />
                    )}
                    <Icon name={item.icon as Parameters<typeof Icon>[0]['name']} size={17} className="shrink-0" />
                    {!collapsed && (
                      <>
                        <span className="flex-1 truncate">{item.label}</span>
                        {item.badge && item.badge > 0 && (
                          <span className="bg-accent/90 text-[#1A2E0F] text-[9px] font-700 px-1.5 py-0.5 rounded-full min-w-[18px] text-center leading-none">
                            {item.badge}
                          </span>
                        )}
                      </>
                    )}
                    {collapsed && item.badge && item.badge > 0 && (
                      <span className="absolute top-0.5 right-0.5 w-1.5 h-1.5 bg-accent rounded-full" />
                    )}
                  </Link>
                );
              })}
            </div>
          );
        })}
      </nav>

      {/* Bottom user info */}
      {!collapsed && (
        <div className="border-t border-white/8 px-3 py-3">
          <div className="flex items-center gap-2.5 px-1">
            <div className="w-7 h-7 rounded-full bg-accent/25 flex items-center justify-center text-accent text-[11px] font-700 shrink-0">
              SM
            </div>
            <div className="min-w-0">
              <p className="text-[12px] font-600 text-white truncate">Arjun Mehta</p>
              <p className="text-[10px] text-white/35 truncate">Store Manager</p>
            </div>
          </div>
        </div>
      )}
    </aside>
  );
}